import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, DisclaimerFooter } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Send, LineChart, Save } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [telegram, setTelegram] = useState("");
  const [studies, setStudies] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then(({ data }) => {
        setTelegram(data.telegram_group || "");
        setStudies((data.tv_studies || []).join("\n"));
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const tv_studies = studies
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      await api.post("/settings", { telegram_group: telegram.trim(), tv_studies });
      toast.success("Zapisano ustawienia", { description: "Zmiany widoczne w Terminalu i na landingu." });
    } catch (e) {
      toast.error("Błąd zapisu", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Ustawienia" subtitle="Link do grupy Telegram i kody wskaźników TradingView." />
      <div className="space-y-6 max-w-2xl">
        <div className="glow-card rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Send className="text-sky-400" size={20} />
            <h2 className="font-display font-semibold text-lg">Link do grupy Telegram</h2>
          </div>
          <Label className="text-xs tracking-widest text-demon-muted">JEDEN LINK DO GRUPY</Label>
          <Input
            data-testid="settings-telegram-input"
            value={telegram}
            onChange={(e) => setTelegram(e.target.value)}
            placeholder="https://t.me/TWOJA_GRUPA"
            className="mt-1.5 bg-black/50 border-violet-900/40 h-11 text-white font-mono"
          />
          <p className="text-[11px] text-demon-muted/70 mt-2">
            Używany na landingu i w Terminalu (przycisk „Dołącz do grupy").
          </p>
        </div>

        <div className="glow-card rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <LineChart className="text-violet-400" size={20} />
            <h2 className="font-display font-semibold text-lg">Kody wskaźników TradingView</h2>
          </div>
          <Label className="text-xs tracking-widest text-demon-muted">JEDEN KOD NA LINIĘ</Label>
          <Textarea
            data-testid="settings-studies-input"
            value={studies}
            onChange={(e) => setStudies(e.target.value)}
            rows={6}
            placeholder={"PUB;p28oOtSBJQa8LJd6b1ytDsu8OUkNbzA1\nSTD;Supertrend\nRSI@tv-basicstudies"}
            className="mt-1.5 bg-black/50 border-violet-900/40 text-white font-mono"
          />
          <div className="text-[11px] text-demon-muted/80 mt-3 space-y-1">
            <p>
              • Wskaźnik społecznościowy: <span className="text-violet-300 font-mono">PUB;&lt;id&gt;</span> — id znajdziesz
              na stronie skryptu (w źródle strony, klucz <span className="font-mono">script_id_part</span>).
            </p>
            <p>
              • Wbudowany: np. <span className="text-violet-300 font-mono">STD;Supertrend</span>,{" "}
              <span className="text-violet-300 font-mono">RSI@tv-basicstudies</span>.
            </p>
            <p>• Uwaga: skrypty invite-only (prywatne) nie wczytają się w osadzonym wykresie.</p>
          </div>
        </div>

        <Button
          data-testid="settings-save-button"
          onClick={save}
          disabled={busy}
          className="h-12 px-8 font-display font-semibold bg-gradient-to-r from-violet-700 to-violet-500 border-0"
        >
          <Save size={16} className="mr-2" /> ZAPISZ
        </Button>
      </div>
      <DisclaimerFooter />
    </div>
  );
}
